import { useCallback } from 'react'
import { Trans } from 'react-i18next'
import uniqueId from 'lodash/uniqueId'
import { Transforms } from 'slate'
import { ReactEditor } from 'slate-react'

import { hasVariableTextFeatureFlag, VariableActionOption, VariableTextFeatureFlag } from '@makyoapp/makyo-common'
import { Date16, Plus16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, DD } from '@makyoapp/makyo-ui'

import { appIconKeys } from '@/shared/icons'

import VariableActionOutput from '../VariableSelectV2/VariableActionOutput'
import VariableColumn from '../VariableSelectV2/VariableColumn'
import VariableColumnData from '../VariableSelectV2/VariableColumnData'
import VariableCurrentListItem from '../VariableSelectV2/VariableCurrentListItem'
import VariableCurrentRow from '../VariableSelectV2/VariableCurrentRow'
import VariableGlobalVariables from '../VariableSelectV2/VariableGlobalVariables'
import VariableIcon from '../VariableSelectV2/VariableIcon'
import VariableImage from '../VariableSelectV2/VariableImage'
import VariablePageContent from '../VariableSelectV2/VariablePageContent'
import VariableProfileData from '../VariableSelectV2/VariableProfileData'
import VariableUniqueID from '../VariableSelectV2/VariableSelectUniqueID'
import VariableSessionID from '../VariableSelectV2/VariableSessionID'
import VariableSingleCell from '../VariableSelectV2/VariableSingleCell'
import { useVariableTextContext } from './VariableTextContext'

function VariableTextColumnData() {
  const { setOpen, editor, forceUserSpecific, tableId } = useVariableTextContext()

  return (
    <VariableColumnData
      tableId={tableId}
      forceUserSpecific={forceUserSpecific}
      onChange={(newVal) => {
        const sel = Object.assign({}, editor.selection)
        Transforms.insertNodes(
          editor,
          {
            type: VariableTextFeatureFlag.ColumnData,
            ...newVal,
            children: [{ text: '' }],
          },
          {
            select: true,
          }
        )
        ReactEditor.focus(editor)
        setTimeout(() => {
          if (sel?.focus) {
            Transforms.select(editor, sel)
          }
        })
        setOpen(false)
      }}
    />
  )
}

function VariableTextIcon() {
  const { editor, setOpen } = useVariableTextContext()
  return (
    <VariableIcon
      actionType={VariableActionOption.None}
      onChange={({ value }) => {
        const sel = Object.assign({}, editor.selection)
        Transforms.insertNodes(
          editor,
          {
            type: VariableTextFeatureFlag.Icon,
            value,
            children: [{ text: '' }],
          },
          {
            //select: true
          }
        )
        ReactEditor.focus(editor)
        setTimeout(() => {
          if (sel?.focus) {
            Transforms.select(editor, sel)
          }
        })
        setOpen(false)
      }}
    />
  )
}

function VariableTextImage() {
  const { editor, setOpen } = useVariableTextContext()
  return (
    <VariableImage
      onHide={() => setOpen(false)}
      onChange={(value) => {
        const sel = Object.assign({}, editor.selection)
        Transforms.insertNodes(
          editor,
          {
            type: VariableTextFeatureFlag.Image,
            value,
            children: [{ text: '' }],
          },
          {
            //select: true
          }
        )
        ReactEditor.focus(editor)
        setTimeout(() => {
          if (sel?.focus) {
            Transforms.select(editor, sel)
          }
        })
      }}
    />
  )
}

function VariableTextSingleCell() {
  const { editor, setOpen, variableColumnOptions } = useVariableTextContext()
  return (
    <VariableSingleCell
      columnTypes={variableColumnOptions.columnTypes}
      onChange={(newVal) => {
        Transforms.insertNodes(editor, {
          type: VariableTextFeatureFlag.SingleCell,
          ...newVal,
          children: [{ text: '' }],
        })
        setOpen(false)
      }}
    />
  )
}

function VariableTextCurrentRow() {
  const { editor, setOpen, variableColumnOptions } = useVariableTextContext()
  return (
    <VariableCurrentRow
      columnTypes={variableColumnOptions.columnTypes}
      onChange={(newVal) => {
        Transforms.insertNodes(editor, {
          type: VariableTextFeatureFlag.CurrentRow,
          ...newVal,
          children: [{ text: '' }],
        })
        setOpen(false)
      }}
    />
  )
}

function VariableTextCurrentListItem() {
  const { editor, setOpen, variableColumnOptions, tableId } = useVariableTextContext()
  return (
    <VariableCurrentListItem
      columnTypes={variableColumnOptions.columnTypes}
      tableId={tableId}
      onChange={(newVal) => {
        Transforms.insertNodes(editor, {
          type: VariableTextFeatureFlag.CurrentListItem,
          ...newVal,
          children: [{ text: '' }],
        })
        setOpen(false)
      }}
    />
  )
}

function VariableTextDateTime() {
  const { editor, setOpen } = useVariableTextContext()
  return (
    <button
      className='gap-2 sdd-item'
      onClick={() => {
        const sel = Object.assign({}, editor.selection)
        Transforms.insertNodes(editor, {
          type: VariableTextFeatureFlag.DateTime,
          children: [{ text: '' }],
        })
        ReactEditor.focus(editor)
        setTimeout(() => {
          if (sel?.focus) {
            Transforms.select(editor, sel)
          }
        })
        setOpen(false)
      }}
    >
      <Date16 />
      <div>
        <Trans>Current Date & Time</Trans>
      </div>
    </button>
  )
}

function VariableTextColumn() {
  const { editor, setOpen, variableColumnOptions, tableId } = useVariableTextContext()
  return (
    <VariableColumn
      columnTypes={variableColumnOptions.columnTypes}
      tableId={tableId}
      onChange={(newVal) => {
        Transforms.insertNodes(editor, {
          type: VariableTextFeatureFlag.Column,
          ...newVal,
          children: [{ text: '' }],
        })
        setOpen(false)
      }}
    />
  )
}

function VariableTextProfileData() {
  const { id, editor, setOpen } = useVariableTextContext()
  return (
    <VariableProfileData
      id={`${id}-profile-data`}
      onChange={(newVal) => {
        Transforms.insertNodes(editor, {
          type: VariableTextFeatureFlag.ProfileData,
          ...newVal,
          children: [{ text: '' }],
        })
        setOpen(false)
      }}
    />
  )
}

function VariableTextSessionID() {
  const { id, editor, setOpen } = useVariableTextContext()
  return (
    <VariableSessionID
      id={`${id}-session-id`}
      onChange={(newVal) => {
        const sel = Object.assign({}, editor.selection)
        Transforms.insertNodes(editor, {
          type: VariableTextFeatureFlag.SessionID,
          ...newVal,
          children: [{ text: '' }],
        })
        ReactEditor.focus(editor)
        setTimeout(() => {
          if (sel?.focus) {
            Transforms.select(editor, sel)
          }
        })
        setOpen(false)
      }}
    />
  )
}

function VariableTextPageContent() {
  const { editor, setOpen } = useVariableTextContext()
  return (
    <VariablePageContent
      onClick={(field: VariableTextFeatureFlag) => {
        const sel = Object.assign({}, editor.selection)
        Transforms.insertNodes(editor, {
          type: field,
          children: [{ text: '' }],
        })
        ReactEditor.focus(editor)
        setTimeout(() => {
          if (sel?.focus) {
            Transforms.select(editor, sel)
          }
        })
        setOpen(false)
      }}
    />
  )
}

function VariableTextActionOutput() {
  const { id, editor, setOpen, options } = useVariableTextContext()
  return (
    <VariableActionOutput
      id={id}
      onChange={(value) => {
        const sel = Object.assign({}, editor.selection)
        Transforms.insertNodes(editor, {
          type: VariableTextFeatureFlag.ActionOutput,
          ...value,
          children: [{ text: '' }],
        })
        ReactEditor.focus(editor)
        setTimeout(() => {
          if (sel?.focus) {
            Transforms.select(editor, sel)
          }
        })
        setOpen(false)
      }}
      onHide={() => {
        setOpen(false)
      }}
      options={options}
    />
  )
}

function VariableTextGlobalVariable() {
  const { id, editor, setOpen } = useVariableTextContext()
  return (
    <VariableGlobalVariables
      id={id}
      onChange={(value) => {
        const sel = Object.assign({}, editor.selection)
        Transforms.insertNodes(editor, {
          type: VariableTextFeatureFlag.GlobalVariable,
          ...value,
          children: [{ text: '' }],
        })
        ReactEditor.focus(editor)
        setTimeout(() => {
          if (sel?.focus) {
            Transforms.select(editor, sel)
          }
        })
        setOpen(false)
      }}
      onHide={() => {
        setOpen(false)
      }}
    />
  )
}

function VariableTextUniqueID() {
  const { id, editor, setOpen } = useVariableTextContext()
  return (
    <VariableUniqueID
      id={`${id}-unique-id`}
      onChange={(newVal) => {
        const sel = Object.assign({}, editor.selection)
        Transforms.insertNodes(editor, {
          type: VariableTextFeatureFlag.UniqueID,
          ...newVal,
          children: [{ text: '' }],
        })
        ReactEditor.focus(editor)
        setTimeout(() => {
          if (sel?.focus) {
            Transforms.select(editor, sel)
          }
        })
        setOpen(false)
      }}
    />
  )
}

function VariableTextNewsletterEmail() {
  const { id, editor, setOpen } = useVariableTextContext()
  return (
    <div
      id={id}
      onClick={() => {
        Transforms.insertNodes(editor, {
          type: VariableTextFeatureFlag.NewsletterEmail,
          children: [{ text: '' }],
        })
        setOpen(false)
      }}
      className='flex-c w-full sdd-item'
    >
      <div className='flex-c w-full pointer-events-none'>
        <MaterialIcon icon={appIconKeys.newsletterEmail} size={16} />
        <div className='pointer-events-none'>
          <Trans>Newsletter Email</Trans>
        </div>
      </div>
    </div>
  )
}

export function VariableTextDropdown() {
  const { editorId, id, icon, featureFlags, open, setOpen, editor, tableId } = useVariableTextContext()

  function getActiveVariableTextCount(open: boolean, featureFlags: number, tableId?: string | null): number {
    if (!open) return 0

    const conditions = [
      hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.CurrentRow),
      hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.CurrentListItem) && !!tableId,
      hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.Column) && !!tableId,
      hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.ColumnData),
      hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.ProfileData),
      hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.SingleCell),
      hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.Icon),
      hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.DateTime),
      hasVariableTextFeatureFlag(
        featureFlags,
        VariableTextFeatureFlag.PageTitle | VariableTextFeatureFlag.PageCoverImage
      ),
      hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.Image),
      hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.SessionID),
      hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.ActionOutput),
    ]

    return conditions.filter(Boolean).length
  }

  const calculateDropdownHeight = useCallback(() => {
    const activeElements = getActiveVariableTextCount(open, featureFlags, tableId)
    const baseHeight = 50
    const itemHeight = 40
    const maxHeight = 300

    const calculatedHeight = baseHeight + activeElements * itemHeight

    return Math.min(calculatedHeight, maxHeight)
  }, [open, featureFlags, tableId])

  const calculateSmartPosition = useCallback(() => {
    if (!open) return 'bottom-start'

    const triggerElement = document.querySelector('[id$="-var-text-dd"]:not([id*="-ddm"])')
    if (!triggerElement) return 'bottom-start'

    const rect = triggerElement.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const dropdownHeight = calculateDropdownHeight()

    const spaceBelow = viewportHeight - rect.bottom
    const spaceAbove = rect.top

    if (spaceBelow < dropdownHeight && spaceAbove && spaceAbove > spaceBelow) {
      return 'top-start'
    }

    return 'bottom-start'
  }, [calculateDropdownHeight, open])

  const placement = calculateSmartPosition()

  return (
    <DD
      id={`${id ?? uniqueId()}-var-text-dd`}
      className='inline-flex! items-center'
      menuAlwaysRender={false}
      toggleNoButton
      menuClassName='w-[220px] z-302'
      menuWrapperClassName='sdd-menu-subc text-m-m text-zinc-700 dark:text-slate-300'
      menuDefaultSize={false}
      placement={placement ?? 'bottom-start'}
      show={open}
      portalMenu
      onToggle={(nextShow, meta) => {
        const evt = meta.originalEvent
        const tgt = evt?.target as HTMLElement
        /*if (preventClose && !nextShow) {
         return
         }*/
        if (
          tgt?.closest('.sdd-search-wrap') ||
          tgt?.className?.includes?.('search-input') ||
          tgt?.tagName === 'INPUT'
        ) {
          return
        } else {
          setOpen(nextShow)
        }
        //setOpen(nextShow)
      }}
      toggle={(active, toggleProps) => {
        return <Button noPadding defaultSize={false} className='w-6 h-6' icon={icon ?? <Plus16 />} {...toggleProps} />
      }}
    >
      {open && hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.CurrentRow) && (
        <VariableTextCurrentRow />
      )}
      {open && hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.CurrentListItem) && tableId && (
        <VariableTextCurrentListItem />
      )}
      {open && hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.Column) && tableId && (
        <VariableTextColumn />
      )}
      {open && hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.ColumnData) && (
        <VariableTextColumnData />
      )}
      {open && hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.ProfileData) && (
        <VariableTextProfileData />
      )}
      {open && hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.SingleCell) && (
        <VariableTextSingleCell />
      )}
      {open && hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.Icon) && <VariableTextIcon />}
      {open && hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.DateTime) && <VariableTextDateTime />}
      {open &&
        hasVariableTextFeatureFlag(
          featureFlags,
          VariableTextFeatureFlag.PageTitle | VariableTextFeatureFlag.PageCoverImage
        ) && <VariableTextPageContent />}
      {open && hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.Image) && <VariableTextImage />}
      {open && hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.SessionID) && <VariableTextSessionID />}
      {open && hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.ActionOutput) && (
        <VariableTextActionOutput />
      )}
      {open && hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.UniqueID) && <VariableTextUniqueID />}
      {open && <VariableTextGlobalVariable />}
      {open && hasVariableTextFeatureFlag(featureFlags, VariableTextFeatureFlag.NewsletterEmail) && (
        <VariableTextNewsletterEmail />
      )}
    </DD>
  )
}
